// Mirrors cloudflare/ocri-worker/src/types.ts (worker = source of truth, snake_case at boundary)
// Frontend converts to camelCase in the api client.

export type UserRole = 'user' | 'admin' | 'superadmin';
export type UserStatus = 'active' | 'suspended' | 'pending_verification';
export type PlanId = 'free' | 'plus' | 'pro';

/** Raw worker shape (snake_case) */
export interface WorkerUser {
  id: string;
  email: string;
  display_name: string | null;
  role: UserRole;
  plan_id: PlanId;
  status: UserStatus;
  device_id: string | null;
  device_last_seen: number | null;
  email_verified: boolean;
  created_at: number;
  updated_at: number;
  expiry_at: number | null;
  stripe_id: string | null;
}

export interface WorkerPlan {
  id: PlanId;
  display_name: string;
  features: string[];
  limits: Record<string, number>;
  price_monthly: number;
  created_at: number;
  updated_at: number;
}

export interface WorkerAuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  user: WorkerUser;
  plan: WorkerPlan;
}

export interface WorkerMeResponse {
  user: WorkerUser;
  plan: WorkerPlan;
}

/** App-facing shape (camelCase) */
export interface User {
  id: string;
  email: string;
  displayName: string | null;
  role: UserRole;
  planId: PlanId;
  status: UserStatus;
  deviceId: string | null;
  deviceLastSeen: number | null;
  emailVerified: boolean;
  createdAt: number;
  updatedAt: number;
  expiryAt: number | null;
  stripeId: string | null;
}

export interface Plan {
  id: PlanId;
  displayName: string;
  description?: string;
  features: string[];
  limits: Record<string, number>;
  priceMonthly: number;
  priceYearly?: number;
  createdAt: number;
  updatedAt: number;
}

export interface AuthSession {
  user: User;
  plan: Plan;
}

/**
 * Flat profile returned by `GET|PATCH /api/user/me` — a narrower shape than
 * `User`: no device binding or email-verification fields.
 */
export interface WorkerProfileResponse {
  id: string;
  email: string;
  display_name: string | null;
  role: UserRole;
  plan_id: PlanId;
  status: UserStatus;
  expiry_at: number | null;
  stripe_id: string | null;
  settings: Record<string, unknown> | null;
  created_at: number;
  updated_at: number;
  plan: WorkerPlan;
}

export interface Profile {
  id: string;
  email: string;
  displayName: string | null;
  role: UserRole;
  planId: PlanId;
  status: UserStatus;
  expiryAt: number | null;
  settings: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
  plan: Plan;
}

export type VoucherValidationResult =
  | { status: 'Valid'; data: { discount_type: string; discount_value: number; message: string } }
  | { status: 'Invalid'; data: { error: string; message: string } };

export function toUser(w: WorkerUser): User {
  return {
    id: w.id,
    email: w.email,
    displayName: w.display_name,
    role: w.role,
    planId: w.plan_id,
    status: w.status,
    deviceId: w.device_id,
    deviceLastSeen: w.device_last_seen,
    emailVerified: w.email_verified,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    expiryAt: w.expiry_at,
    stripeId: w.stripe_id,
  };
}

export function toPlan(w: WorkerPlan): Plan {
  return {
    id: w.id,
    displayName: w.display_name,
    features: w.features,
    limits: w.limits,
    priceMonthly: w.price_monthly,
    priceYearly: w.price_monthly ? w.price_monthly * 12 : undefined,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
  };
}

export function toProfile(w: WorkerProfileResponse): Profile {
  return {
    id: w.id,
    email: w.email,
    displayName: w.display_name,
    role: w.role,
    planId: w.plan_id,
    status: w.status,
    expiryAt: w.expiry_at,
    settings: w.settings ?? {},
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    plan: toPlan(w.plan),
  };
}